import { detectPlatform } from "../platform.js";

/**
 * Whether the phone-style touch UX applies: the Capacitor (Android) app, or —
 * for browser-devtools testing of the same code paths — a coarse-pointer
 * device at a phone-ish width. Evaluated once at module load; the platform
 * never changes mid-session, and devtools device emulation reloads anyway.
 */
let cached: boolean | null = null;

/** Ghost-demo boot only: pin the UX mode before any component evaluates it.
 *  Deliberately NOT the localStorage hatch — that key is shared with the host
 *  app on this origin and must never be written by demo code. */
export function setMobileUxForDemo(v: boolean): void {
  cached = v;
  // isCoarsePointer() derives from isMobileUx(), so it must re-derive too.
  coarse = null;
}

export function isMobileUx(): boolean {
  if (cached !== null) return cached;
  if (typeof window === "undefined") return (cached = false);
  cached =
    detectPlatform() === "capacitor" ||
    (window.matchMedia("(pointer: coarse)").matches && window.innerWidth < 768) ||
    // Dev escape hatch: force the touch UX from a desktop browser console.
    localStorage.getItem("mobileUx") === "1";
  return cached;
}

/** Hook form for components (static value — see isMobileUx). */
export function useMobileUx(): boolean {
  return isMobileUx();
}

let coarse: boolean | null = null;

/**
 * Pure input-capability check, NOT the phone-UX gate: a coarse pointer at ANY
 * width (touch laptops, tablets in landscape) needs finger-sized hit targets
 * even where `isMobileUx()` — which is width-gated — stays false.
 */
export function isCoarsePointer(): boolean {
  if (coarse !== null) return coarse;
  if (typeof window === "undefined") return (coarse = false);
  return (coarse = isMobileUx() || window.matchMedia("(pointer: coarse)").matches);
}

/** Hook form for components (static value — see isCoarsePointer). */
export function useCoarsePointer(): boolean {
  return isCoarsePointer();
}
